// Owns the org dashboard's stats/filters/charts. The multi-select and flatpickr
// controllers are separate -- this only listens for their change events.
import ApplicationController from './application_controller'
import Chart from 'chart.js/auto'

import { resolveDatasetColors } from './chart/theme'
import { SmoothLineVariant } from './chart/variants/smooth_line_variant'
import { CategoryBreakdownVariant } from './chart/variants/category_breakdown_variant'

const PALETTE = ['#3399FF', '#EE6840', '#FFCE56', '#4BC0C0', '#9966FF']

export default class extends ApplicationController {
  static targets = [
    'datePresetSelect',
    'donateAmount',
    'purchasesAmount',
    'hoursAmount',
    'totalRaisedAmount',
    'loader',
    'lineChartCanvas',
    'pieChartCanvas',
  ]

  static values = {
    refreshUrl: String,
    csvUrl: String,
    lineChartData: Array,
    pieChartData: Object,
    selectedValue: String,
    selectedDateRangePicker: String,
  }

  connect() {
    this.selectedOppIds = this.selectedValueValue
    this.selectedDateRangeText = this.selectedDateRangePickerValue
    this.renderLineChart(this.buildLineChartData(this.lineChartDataValue))
    this.renderPieChart(this.buildPieChartData(this.pieChartDataValue))
  }

  disconnect() {
    this.lineChart?.destroy()
    this.pieChart?.destroy()
  }

  oppSelectChanged(event) {
    this.selectedOppIds = event.detail.value
    this.refresh()
  }

  dateRangeChanged(event) {
    this.selectedDateRangeText = event.detail.formatted
    this.datePresetSelectTarget.value = 'custom_date'
    this.refresh()
  }

  presetChanged(event) {
    event.stopPropagation()
    const selectedPreset = event?.detail?.value || this.datePresetSelectTarget.value

    if (selectedPreset === 'custom_date') {
      // Open flatpickr modal when 'Custom' is selected
      this.dispatch('open-date-range')
      return
    }

    // Clear custom range string if switching back to a preset
    this.selectedDateRangeText = ''
    this.refresh()
  }

  async refresh() {
    this.showLoader()

    const url = new URL(this.refreshUrlValue, window.location.origin)
    const params = this.currentParams()
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value))
    url.searchParams.set('format', 'json')

    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
      const data = await response.json()

      this.updateStats(data)
      this.renderLineChart(this.buildLineChartData(data.line_chart_data))
      this.renderPieChart(this.buildPieChartData(data.pie_chart_data))
    } catch (error) {
      console.error('[organization-dashboard] refresh failed', error)
    } finally {
      this.hideLoader()
    }
  }

  async downloadCsv() {
    const url = new URL(this.csvUrlValue, window.location.origin)
    const params = this.currentParams()
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value))

    try {
      const response = await fetch(url.toString())
      const blob = await response.blob()

      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = 'data.csv'
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('[organization-dashboard] CSV download failed', error)
    }
  }

  currentParams() {
    const selectedValue = this.selectedOppIds
    const selectedDateRange = this.datePresetSelectTarget.value

    return {
      selectedValue: Array.isArray(selectedValue) ? selectedValue.join(',') : selectedValue || '',
      selectedDateRange,
      selectedDateRangePicker: selectedDateRange === 'custom_date' ? this.selectedDateRangeText || '' : '',
    }
  }

  formatCurrency(value) {
    return this.normalizeAmount(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  }

  normalizeAmount(value) {
    if (Array.isArray(value)) {
      return value.reduce((sum, entry) => sum + (Number(entry) || 0), 0)
    }
    return Number(value) || 0
  }

  updateStats(data) {
    this.donateAmountTarget.textContent = this.formatCurrency(data.donateSum)
    this.purchasesAmountTarget.textContent = this.formatCurrency(data.purchasesSum)
    this.hoursAmountTarget.textContent = this.normalizeAmount(data.hours).toFixed(1)
  }

  buildLineChartData(series = []) {
    const labelSet = new Set()
    series.forEach((entry) => (entry.data || []).forEach(([label]) => labelSet.add(String(label))))
    const labels = Array.from(labelSet)

    const datasets = series.map((entry, index) => {
      const byLabel = new Map((entry.data || []).map(([label, value]) => [String(label), this.normalizeAmount(value)]))
      return {
        label: entry.name,
        data: labels.map((label) => byLabel.get(label) ?? 0),
        borderColor: entry.color || PALETTE[index % PALETTE.length],
        backgroundColor: entry.color || PALETTE[index % PALETTE.length],
      }
    })

    return { labels, datasets }
  }

  buildPieChartData(breakdown = {}) {
    const labels = Object.keys(breakdown)
    const values = labels.map((label) => this.normalizeAmount(breakdown[label]))
    const total = values.reduce((sum, value) => sum + value, 0)

    return {
      labels,
      datasets: [{ data: values, backgroundColor: labels.map((_, index) => PALETTE[index % PALETTE.length]) }],
      total,
    }
  }

  renderLineChart({ labels, datasets }) {
    resolveDatasetColors(datasets)
    SmoothLineVariant.applyDataDefaults(datasets)

    const options = SmoothLineVariant.buildOptions({
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            padding: 24,
            usePointStyle: true,
            pointStyle: 'rect',
            boxWidth: 12,
            boxHeight: 12,
          },
        },
        title: { text: 'Revenue Over Time' },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context) => `${context.dataset.label}: ${this.formatCurrency(context.parsed.y)}`,
          },
        },
      },
      scales: {
        y: { ticks: { callback: (value) => this.formatCurrency(value) } },
      },
    })

    this.lineChart?.destroy()
    this.lineChart = new Chart(this.lineChartCanvasTarget, {
      type: SmoothLineVariant.chartType,
      data: { labels, datasets },
      options,
    })
  }

  renderPieChart({ labels, datasets, total }) {
    resolveDatasetColors(datasets)
    CategoryBreakdownVariant.applyDataDefaults(datasets)

    // Amount + label render as real HTML above the canvas now (see the haml
    // partial) instead of Chart.js's canvas-drawn title, so they can use
    // different sizes/weights -- just keep this target in sync with it.
    this.totalRaisedAmountTarget.textContent = this.formatCurrency(total)

    const options = CategoryBreakdownVariant.buildOptions({
      plugins: {
        title: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => `${context.label}: ${this.formatCurrency(context.parsed)}`,
          },
        },
      },
    })

    this.pieChart?.destroy()
    this.pieChart = new Chart(this.pieChartCanvasTarget, {
      type: CategoryBreakdownVariant.chartType,
      data: { labels, datasets },
      options,
    })
  }

  showLoader() {
    this.loaderTarget.classList.remove('d-none')
  }

  hideLoader() {
    this.loaderTarget.classList.add('d-none')
  }
}
